import { TOB_INSURERS, isValidMorganPriceTobId, isValidOptimumGlobalTobId, isValidVumiTobId, isValidMyHealthTobId } from './tobInsurers';

// How a quoted plan id maps onto its column in the universal_output TOB spreadsheet, shared by
// the Auto Quotation page and the Quotation page's Plan Comparison dialog.

const TOB_PLAN_IDS = new Set(TOB_INSURERS.flatMap(insurer => insurer.plans.map(plan => plan.id)));

// Whether the TOB id has a column in the universal_output TOB spreadsheet (custom TOB plans don't).
export function isUniversalTobId(tobId) {
    if (!tobId) return false;
    return TOB_PLAN_IDS.has(tobId) || isValidMorganPriceTobId(tobId) || isValidOptimumGlobalTobId(tobId) || isValidVumiTobId(tobId) || isValidMyHealthTobId(tobId);
}

export const TOB_MODULES = [
    { name: 'Outpatient Benefits', start: 6, end: 17 },
    { name: 'Inpatient Benefits', start: 18, end: 28 },
    { name: 'Further Benefits', start: 29, end: 41 },
    { name: 'Maternity Benefits', start: 42, end: 47 },
    { name: 'Dental Benefits', start: 48, end: 52 },
    { name: 'Optical Benefits', start: 53, end: 57 },
    { name: 'Medical Evacuation', start: 58, end: 58 },
    { name: 'Medical Repatriation', start: 59, end: 59 },
];

const PLAN_TO_TOB_MAPPING = {
    'ihhp_hp': 'ihhp_hospital_plan',
    'ihhp_m1': 'ihhp_non_hospitalisation_benefits',
    'ihhp_m2': 'ihhp_medicine_appliances',
    'ihhp_m4a': 'ihhp_dental_optical_a',
    'ihhp_m4b': 'ihhp_dental_optical_b',
    // The Core TOB column already lists OP as an optional add-on, so Core with OP shares it.
    'pacific_cross_primary_care_plan_core_with_op': 'pacific_cross_primary_care_plan_core',
};

// Pacific Cross Select plan ids only carry the tier ("pacific_cross_select_plus"); each room
// type has its own TOB column, so the room type picked in the dialog completes the TOB id.
const PACIFIC_CROSS_SELECT_ROOM_TYPE_SLUGS = {
    'Ward': 'ward',
    'Semi-Private': 'semi_private',
    'Private 2M': 'private_2m',
    'Private 3M': 'private_3m',
    'Private 5M': 'private_5m',
};

function normalizePacificCrossSelectTobId(planId, payloads) {
    if (planId !== 'pacific_cross_select_plus' && planId !== 'pacific_cross_select_standard') return null;
    const roomTypeSlug = PACIFIC_CROSS_SELECT_ROOM_TYPE_SLUGS[payloads?.room_type];
    return roomTypeSlug ? `${planId}_${roomTypeSlug}` : null;
}

// IHHP module combinations (e.g. "ihhp_hp_m1_m2") use the TOB of their highest selected module
const IHHP_MODULE_TOB_PRIORITY = [
    ['m4b', 'ihhp_dental_optical_b'],
    ['m4a', 'ihhp_dental_optical_a'],
    ['m2', 'ihhp_medicine_appliances'],
    ['m1', 'ihhp_non_hospitalisation_benefits'],
    ['hp', 'ihhp_hospital_plan'],
];

function normalizeIhhpTobId(planId) {
    const match = /^ihhp_((?:hp|m1|m2|m3|m4a|m4b)(?:_(?:hp|m1|m2|m3|m4a|m4b))*)$/.exec(planId);
    if (!match) return null;
    const modules = match[1].split('_');
    return IHHP_MODULE_TOB_PRIORITY.find(([key]) => modules.includes(key))?.[1] || null;
}

export function normalizeTobId(planId, payloads = null) {
    let normalizedId = PLAN_TO_TOB_MAPPING[planId] || normalizeIhhpTobId(planId) || normalizePacificCrossSelectTobId(planId, payloads) || planId;

    // if (normalizedId.startsWith('my_health_philippines') || normalizedId.startsWith('my_health_singapore') || normalizedId.startsWith('my_health_indonesia')) {
    //     normalizedId = normalizedId.replace(/(_op|_ma|_dn)$/, '');
    // }

    return normalizedId;
}

// Currency (row 4) and Area of Coverage (row 5) cells store their value under column key '1'
export function setTobTableRowValue(table, rowNum, value) {
    const rowKey = String(rowNum);
    const currentRow = table?.[rowKey];
    let updatedRow;

    if (!currentRow || typeof currentRow !== 'object' || Array.isArray(currentRow)) {
        updatedRow = { '1': { cell_value: value } };
    } else {
        const existingCell = currentRow['1'] || currentRow;
        if (existingCell && typeof existingCell === 'object') {
            updatedRow = { ...currentRow, '1': { ...existingCell, cell_value: value } };
        } else {
            updatedRow = { ...currentRow, '1': { cell_value: value } };
        }
    }

    return {
        ...(table || {}),
        [rowKey]: updatedRow,
    };
}
